import { useRef } from 'react';
import { MoreHorizontal, Sparkles } from 'lucide-react';
import { Popover, cn } from '@nw/ui';
import type { WindowTool } from '@nw/web-platform';

/**
 * Ask, under the left thumb. Tap to type or choose; hold to start speaking straight away.
 */
export function AskOrb({ onAsk, listening }: { onAsk: (listen: boolean) => void; listening?: boolean }) {
  const hold = useRef<number | null>(null);
  const held = useRef(false);
  return (
    <button
      type="button"
      className={cn('nw-ask-orb', listening && 'nw-ask-orb-live')}
      aria-label="Ask: tap to type, hold to speak"
      aria-keyshortcuts="Control+K Meta+K"
      onPointerDown={(e) => {
        if (e.button > 0) return;
        held.current = false;
        hold.current = window.setTimeout(() => {
          held.current = true;
          onAsk(true);
        }, 420);
      }}
      onPointerUp={() => {
        if (hold.current) window.clearTimeout(hold.current);
        if (!held.current) onAsk(false);
      }}
      onPointerLeave={() => hold.current && window.clearTimeout(hold.current)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onAsk(false);
        }
      }}
    >
      <span className="nw-ask-orb-glow" aria-hidden />
      <Sparkles className="relative size-6" />
    </button>
  );
}

/**
 * The space's own actions, under the right thumb. The first is the main action; the rest are
 * round buttons; beyond three, they fold into "More".
 */
export function SpaceActions({ tools }: { tools: WindowTool[] }) {
  if (!tools.length) return <span className="nw-space-actions" aria-hidden />;
  const [main, ...rest] = tools as [WindowTool, ...WindowTool[]];
  const MainIcon = main.icon;
  const shown = rest.length > 2 ? rest.slice(0, 1) : rest;
  const folded = rest.length > 2 ? rest.slice(1) : [];
  const round = (t: WindowTool) => (
    <button key={t.id} type="button" onClick={t.run} aria-pressed={t.pressed} aria-label={t.label} aria-keyshortcuts={t.shortcut} className={cn('nw-action-round', t.pressed && 'nw-action-on')} data-tip={t.label}>
      {t.icon ? <t.icon className="size-5" /> : t.label.slice(0, 1)}
    </button>
  );
  return (
    <div role="toolbar" aria-label="Space actions" className="nw-space-actions">
      {folded.length ? (
        <Popover.Root>
          <Popover.Trigger className="nw-action-round" aria-label="More actions" data-tip="More">
            <MoreHorizontal className="size-5" />
          </Popover.Trigger>
          <Popover.Portal>
            <Popover.Content side="top" align="end" sideOffset={12} className="nw-popover z-50 grid min-w-56 gap-1 p-2">
              {folded.map((t) => (
                <Popover.Close key={t.id} asChild>
                  <button type="button" onClick={t.run} aria-pressed={t.pressed} className="flex min-h-12 items-center gap-3 rounded-[14px] px-3 text-start text-[15px] font-medium hover:bg-[var(--nw-surface-sunk)]">
                    {t.icon ? <t.icon className="size-5" /> : null}
                    {t.label}
                  </button>
                </Popover.Close>
              ))}
            </Popover.Content>
          </Popover.Portal>
        </Popover.Root>
      ) : null}
      {shown.map(round)}
      <button type="button" onClick={main.run} aria-pressed={main.pressed} aria-keyshortcuts={main.shortcut} className={cn('nw-action-main', main.pressed && 'nw-action-on')}>
        {MainIcon ? <MainIcon className="size-5" /> : null}
        <span>{main.label}</span>
      </button>
    </div>
  );
}
